import { useState } from 'react';
import dayjs from 'dayjs';

import { useListZeroStatusQuery } from 'modules/App/queries';
import { useDashboardData } from 'modules/Dashboard/queries';

import OverviewUi from './Overview.ui';
import { DiscrepancyKey, OverviewData } from './Overview.types';

/*
 * The proposed endorsement overview, built beside the current dashboard while
 * the business reviews it. It asks for exactly what the dashboard asks for,
 * with the same query keys, so the two share one cache and always agree.
 */
export default function Overview() {
  const [openDiscrepancy, setOpenDiscrepancy] = useState<DiscrepancyKey | null>(
    null
  );

  const queries = useDashboardData(
    { startDate: dayjs().subtract(7, 'days'), endDate: dayjs() },
    { startDate: dayjs().subtract(7, 'days'), endDate: dayjs() }
  );

  const [
    listZeroQuery,
    listOneQuery,
    listTwoQuery,
    listThreeQuery,
    ,
    listFiveQuery,
    listStatusQuery,
    followUpQuery,
    discrepancyQuery,
  ] = queries;

  // Only fetched for users who can block source data, by the App shell; the
  // dashboard reads the same cache, so it never asks on its own.
  const listZeroStatusQuery = useListZeroStatusQuery();
  const followUps = followUpQuery.data?.data.data;

  const listOne = listOneQuery.data?.data;
  const listTwo = listTwoQuery.data?.data;
  const listFive = listFiveQuery.data?.data;
  const lastApi = listZeroQuery.data?.data.lastAPI;
  const discrepancies = discrepancyQuery.data?.data;

  const data: OverviewData = {
    listOne: {
      total: listOne?.total ?? 0,
      pending: listOne?.types['open-pending'] ?? 0,
      clean: listOne?.types['open-clean'] ?? 0,
      errors: listOne?.types['open-error'] ?? 0,
      byType: listOne?.data ?? [],
    },
    followUps: {
      total: followUps?.total ?? 0,
      additionErrors: followUps?.AD.error ?? 0,
      deletionErrors: followUps?.DE.error ?? 0,
    },
    listTwo: {
      total: listTwo?.total ?? 0,
      pending: listTwo?.types['open-pending'] ?? 0,
      clean: listTwo?.types['open-clean'] ?? 0,
      errors: listTwo?.types['open-error'] ?? 0,
      byType: listTwo?.data ?? [],
    },
    insured: {
      total: listThreeQuery.data?.data.totalInsured ?? 0,
      // totalLife counts those WITH a life cover, as the old dashboard reads it.
      withoutLife: Math.max(
        (listThreeQuery.data?.data.totalInsured ?? 0) -
          (listThreeQuery.data?.data.totalLife ?? 0),
        0
      ),
    },
    invoices: {
      awaitingApproval: listFive?.needapproval ?? 0,
      awaitingApprovalPrincipals: listFive?.needapprovalPrincipals ?? 0,
      awaitingApprovalDependents: listFive?.needapprovalDependents ?? 0,
      withAzadea: listFive?.underAzadeaReview ?? 0,
      withAzadeaPrincipals: listFive?.underAzadeaReviewPrincipals ?? 0,
      withAzadeaDependents: listFive?.underAzadeaReviewDependents ?? 0,
    },
    health: {
      apiOk: !!listStatusQuery.data?.data.success,
      sourceSyncOk: !!lastApi?.status,
      lastSync: lastApi?.response.endTime || '',
      sourceRecords: lastApi?.response.dataReceived ?? 0,
      // Undefined when this user's shell never asked, so nothing is claimed.
      sourceDataBlocked:
        listZeroStatusQuery.data === undefined
          ? undefined
          : !listZeroStatusQuery.data.data.data.enabled,
      discrepancies: {
        list0Discrepancy: discrepancies?.list0Discrepancy ?? [],
        list3Discrepancy: discrepancies?.list3Discrepancy ?? [],
        policiesDiscrepancy: discrepancies?.policiesDiscrepancy ?? [],
        rbacPolicyHolderDiscrepancy:
          discrepancies?.rbacPolicyHolderDiscrepancy ?? [],
      },
    },
  };

  // A failed request must never read as a zero, so each figure knows whether
  // its source answered.
  const errors = {
    listOne: listOneQuery.isError,
    listTwo: listTwoQuery.isError,
    insured: listThreeQuery.isError,
    invoices: listFiveQuery.isError,
    followUps: followUpQuery.isError,
    api: listStatusQuery.isError,
    sync: listZeroQuery.isError,
    discrepancies: discrepancyQuery.isError,
  };

  const isLoading = queries.some((query) => query.isLoading);

  function handleRefresh() {
    queries.forEach((query) => query.refetch());
  }

  return (
    <OverviewUi
      isLoading={isLoading}
      data={data}
      errors={errors}
      openDiscrepancy={openDiscrepancy}
      handlers={{
        handleRefresh,
        handleDiscrepancyOpen: setOpenDiscrepancy,
        handleDiscrepancyClose: () => setOpenDiscrepancy(null),
      }}
    />
  );
}
